<template>
  <div class="picklist-container">
    <div
      class="picklist-trigger"
      :class="{ open: isOpen, disabled: disabled }"
      @click="toggleDropdown"
      tabindex="0"
      role="combobox"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      @keydown.enter="toggleDropdown"
      @keydown.space.prevent="toggleDropdown"
      @keydown.escape="closeDropdown"
    >
      <span class="selected-option">
        {{ selectedOption?.label || placeholder || $t('common.selectOption') }}
      </span>
      <span class="dropdown-arrow" :class="{ rotated: isOpen }">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </span>
    </div>

    <div v-if="isOpen" class="picklist-dropdown" role="listbox">
      <div
        v-for="option in options"
        :key="option.value"
        class="picklist-option"
        role="option"
        :aria-selected="option.value === modelValue"
        :aria-disabled="option.disabled"
        :class="{
          selected: option.value === modelValue,
          disabled: option.disabled,
        }"
        @click="selectOption(option)"
        @mouseenter="hoveredOption = option.value"
        @mouseleave="hoveredOption = null"
      >
        <div class="option-content">
          <div class="option-label">{{ option.label }}</div>
          <div v-if="option.description" class="option-description">
            {{ option.description }}
          </div>
          <div v-if="option.disabled && option.disabledMessage" class="disabled-message">
            {{ option.disabledMessage }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
  options: {
    type: Array,
    required: true,
    // Expected format: [{ value: string, label: string, description?: string, disabled?: boolean, disabledMessage?: string }]
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const isOpen = ref(false);
const hoveredOption = ref(null);

const selectedOption = computed(() => {
  return props.options.find((option) => option.value === props.modelValue);
});

function toggleDropdown() {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
}

function closeDropdown() {
  isOpen.value = false;
}

function selectOption(option) {
  if (option.disabled) return;
  emit("update:modelValue", option.value);
  closeDropdown();
}

function handleClickOutside(event) {
  if (!event.target.closest(".picklist-container")) {
    closeDropdown();
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.picklist-container {
  position: relative;
  width: 100%;
}

.picklist-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gap-lg);
  border: var(--border-width-normal) solid var(--border-color-input);
  border-radius: var(--border-radius-xs);
  margin-bottom: var(--margin-md);
  transition: var(--transition-border);
  padding: var(--padding-md);
  background: transparent;
  color: var(--color-white);
  cursor: pointer;
  box-sizing: border-box;
}

.picklist-trigger:hover:not(.disabled) {
  border: var(--border-width-normal) solid var(--border-color-input-hover);
}

.picklist-trigger.open {
  border: var(--border-width-normal) solid var(--border-color-input-focus);
}

.picklist-trigger.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.picklist-trigger:focus {
  outline: none;
  border: var(--border-width-normal) solid var(--border-color-input-focus);
}

.selected-option {
  flex-grow: 1;
  text-align: left;
  color: var(--color-white);
  padding: var(--padding-md);
  box-sizing: content-box;
  font-size: var(--font-size-body);
  line-height: var(--line-height-body-alt);
}

.dropdown-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-transform);
  color: var(--color-white-60);
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.picklist-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--bg-dropdown);
  border: var(--border-width-normal) solid var(--border-color-input);
  border-radius: var(--border-radius-md);
  z-index: var(--z-index-dropdown);
  max-height: var(--max-height-dropdown);
  overflow-y: auto;
  box-shadow: var(--shadow-dropdown);
}

.picklist-option {
  padding: var(--padding-lg);
  cursor: pointer;
  transition: var(--transition-bg);
  border-bottom: var(--border-width-thin) solid var(--color-white-05);
}

.picklist-option:last-child {
  border-bottom: none;
}

.picklist-option:hover:not(.disabled) {
  background: var(--color-white-10);
}

.picklist-option.selected {
  background: var(--color-white-15);
}

.picklist-option.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.option-content {
  display: flex;
  flex-direction: column;
  gap: var(--gap-sm);
}

.option-label {
  color: var(--color-white);
  font-weight: var(--font-weight-medium);
}

.option-description {
  color: var(--color-white-70);
  font-size: var(--font-size-medium);
  line-height: var(--line-height-medium);
}

.disabled-message {
  color: var(--color-error-light);
  font-size: var(--font-size-small);
  font-style: italic;
  margin-top: var(--margin-xs);
}

.picklist-dropdown::-webkit-scrollbar {
  width: var(--scrollbar-width);
}

.picklist-dropdown::-webkit-scrollbar-track {
  background: var(--color-white-05);
  border-radius: var(--border-radius-xs);
}

.picklist-dropdown::-webkit-scrollbar-thumb {
  background: var(--color-white-20);
  border-radius: var(--border-radius-xs);
}

.picklist-dropdown::-webkit-scrollbar-thumb:hover {
  background: var(--color-white-30);
}
</style>
